import { cn, DropdownMenu, IconButton, Spinner, Tooltip } from "@z/ui";
import { ChevronDown, Mic, Square } from "lucide-react";
import type { DictationControls } from "./useDictation";

export { useDictation, type DictationControls, type UseDictationOptions } from "./useDictation";

export type DictationButtonProps = Pick<
  DictationControls,
  "status" | "toggle" | "devices" | "refreshDevices"
> & {
  /** The remembered input; the system default when undefined. */
  deviceId?: string | undefined;
  /** Renders the input picker when provided. `undefined` selects the system default. */
  onSelectDevice?: ((deviceId: string | undefined) => void) | undefined;
  className?: string;
};

const LABELS = {
  idle: "Start dictation",
  error: "Start dictation",
  requesting_mic: "Cancel dictation",
  connecting: "Cancel dictation",
  listening: "Stop dictation",
  stopping: "Stopping dictation",
} as const;

/** Toolbar mic toggle with an input picker; the live phrase previews inline at the caret. */
export function DictationButton({
  status,
  toggle,
  devices,
  refreshDevices,
  deviceId,
  onSelectDevice,
  className,
}: DictationButtonProps) {
  const label = LABELS[status];
  const listening = status === "listening";
  const busy = status === "requesting_mic" || status === "connecting" || status === "stopping";
  const known = deviceId === undefined || devices.some((d) => d.deviceId === deviceId);
  return (
    <div className={cn("flex min-w-0 shrink items-center gap-1.5", className)}>
      <div className="flex items-center">
        <Tooltip content={label} shortcutId="editor.dictate">
          <IconButton
            label={label}
            size="sm"
            aria-pressed={listening}
            disabled={status === "stopping"}
            className="aria-pressed:bg-accent-muted aria-pressed:text-accent"
            onMouseDown={(event) => event.preventDefault()}
            onClick={toggle}
          >
            {busy ? (
              <Spinner size="sm" label={label} />
            ) : listening ? (
              <Square className="fill-current" />
            ) : (
              <Mic />
            )}
          </IconButton>
        </Tooltip>
        {onSelectDevice && (
          <DropdownMenu.Root
            onOpenChange={(open) => {
              if (open) refreshDevices();
            }}
          >
            <DropdownMenu.Trigger
              render={
                <IconButton
                  label="Microphone input"
                  size="sm"
                  className="-ml-1 w-4"
                  onMouseDown={(event) => event.preventDefault()}
                >
                  <ChevronDown className="size-3!" />
                </IconButton>
              }
            />
            <DropdownMenu.Content aria-label="Microphone input" align="end">
              <DropdownMenu.Label>Microphone</DropdownMenu.Label>
              <DropdownMenu.CheckboxItem
                checked={deviceId === undefined}
                onCheckedChange={() => onSelectDevice(undefined)}
              >
                System default
              </DropdownMenu.CheckboxItem>
              {devices.map((device) => (
                <DropdownMenu.CheckboxItem
                  key={device.deviceId}
                  checked={device.deviceId === deviceId}
                  onCheckedChange={() => onSelectDevice(device.deviceId)}
                >
                  <span className="truncate">{device.label}</span>
                </DropdownMenu.CheckboxItem>
              ))}
              {!known && (
                <DropdownMenu.CheckboxItem
                  checked
                  onCheckedChange={() => onSelectDevice(undefined)}
                >
                  <span className="truncate text-fg-muted">Remembered device (not connected)</span>
                </DropdownMenu.CheckboxItem>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        )}
      </div>
    </div>
  );
}
